import React from "react";
import { X, GitCompare, CheckCircle2 } from "lucide-react";

const ScenarioComparisonModal = ({ isOpen, onClose, isDarkMode, scenarios, getScenarioComparison }) => {
  if (!isOpen) return null;

  const comparison = getScenarioComparison();
  const card = isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const headerBg = isDarkMode ? "bg-gray-900/50" : "bg-gray-50";

  const getBestValue = (key, higher = true) => {
    const values = comparison.map((s) => s[key]);
    return higher ? Math.max(...values) : Math.min(...values);
  };

  const cellClass = (value, best, higher = true) => {
    const isBest = higher ? value === best : value === best;
    return isBest ? "text-emerald-500 font-bold" : "";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-3xl rounded-2xl border shadow-2xl ${card}`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <GitCompare size={20} className="text-indigo-500" />
            Scenario Comparison
          </h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={headerBg}>
                <tr>
                  <th className="text-left py-3 px-4 font-medium rounded-l-lg">Metric</th>
                  {comparison.map((s, idx) => (
                    <th 
                      key={s.key} 
                      className={`text-center py-3 px-4 font-medium ${idx === comparison.length - 1 ? "rounded-r-lg" : ""}`}
                    >
                      {s.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr>
                  <td className="py-4 px-4 text-gray-500 dark:text-gray-400">Average Trainability</td>
                  {comparison.map((s) => (
                    <td key={s.key} className={`text-center py-4 px-4 ${cellClass(s.avgTrainability, getBestValue("avgTrainability"))}`}>
                      <span className="text-lg">{s.avgTrainability}/10</span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="py-4 px-4 text-gray-500 dark:text-gray-400">Players at Risk</td>
                  {comparison.map((s) => (
                    <td key={s.key} className={`text-center py-4 px-4 ${cellClass(s.playersAtRisk, getBestValue("playersAtRisk", false), false)}`}>
                      <span className="text-lg">{s.playersAtRisk}</span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="py-4 px-4 text-gray-500 dark:text-gray-400">Optimal Players</td>
                  {comparison.map((s) => (
                    <td key={s.key} className={`text-center py-4 px-4 ${cellClass(s.optimalPlayers, getBestValue("optimalPlayers"))}`}>
                      <span className="text-lg">{s.optimalPlayers}</span>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className={`mt-4 pt-4 border-t text-xs flex items-center gap-4 ${isDarkMode ? "border-gray-700 text-gray-400" : "border-gray-200 text-gray-500"}`}>
            <span className="flex items-center gap-1">
              <CheckCircle2 size={12} className="text-emerald-500" />
              Best value
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenarioComparisonModal;
